<template lang="pug">
  .related-products(:class="{ [variant]: variant }")
    p.related-products-title.f-primary.semi-bold(:class="{ 'xs-f-xsmall': variant !== 'preview', 'xs-f-xxsmall': variant === 'preview' }") {{ title }}
    template(v-if="visibleProducts && visibleProducts.length")
      .related-products-list(v-if="variant === 'preview'")
        .related-products-item(v-for="product in firstTwoProducts" :key="product.id")
          .related-products-item-inner
            .related-products-item-image
              a(v-if="product.url_key && product.hasContent" :href="productUrl(product.url_key)" target="_self")
                img.prod-thumb(:class="{ preview: variant === 'preview' }" :src="imgSrc(product)" :alt="altText(product)")

              img.prod-thumb(v-else :class="{ preview: variant === 'preview' }" :src="imgSrc" :alt="altText(product)")

            .related-products-item-desc
              a.related-products-item-name.f-primary.semi-bold.xs-f-xxsmall(
                :href="productUrl(product.url_key)" target="_self"
              ) {{ product.name }}
              p.related-products-item-price.f-primary.semi-bold.xs-f-xxsmall {{ $currency(product.price) }}

      .related-products-list(v-if="variant === 'inline'")
        SimpleCarouselSSR(ref="slick" :options="carouselOptions" theme="floating-arrows")
          template(#left-nav-content="")
            .arrow-container
              MrIcon(name="left-pointing-arrow" height="0.8rem")
              span.ada-tooltip previous

          template(#right-nav-content="")
            .arrow-container
              MrIcon(name="arrow-pointing-right" height="0.8rem")
              span.ada-tooltip next

          .related-products-item(v-for="product in visibleProducts" :key="product.id")
            .related-products-item-inner
              .related-products-item-image
                a(v-if="product.url_key && product.hasContent" :href="productUrl(product.url_key)" target="_self")
                  img.prod-thumb(:src="imgSrc(product)" :alt="altText(product)")

                img.prod-thumb(v-else :src="imgSrc" :alt="altText(product)")

              .related-products-item-desc
                a.related-products-item-name.f-primary.semi-bold.xs-f-xsmall(
                  :href="productUrl(product.url_key)" target="_self"
                ) {{ product.name }}
                p.related-products-item-price.f-primary.semi-bold.xs-f-xsmall {{ $currency(product.price) }}

      .related-products-list.stacked(v-else-if="variant === 'stacked'")
        .related-products-item(v-for="product in visibleProducts" :key="product.id")
          .related-products-item-inner
            .related-products-item-image
              a(v-if="product.url_key && product.hasContent" :href="productUrl(product.url_key)" target="_self")
                img.prod-thumb(:src="imgSrc(product)" :alt="altText(product)")

              img.prod-thumb(v-else :src="imgSrc" :alt="altText(product)")

            .related-products-item-desc
              a.related-products-item-name.f-primary.semi-bold.xs-f-xsmall(
                :href="productUrl(product.url_key)" target="_self"
              ) {{ product.name }}
              p.related-products-item-price.f-primary.semi-bold.xs-f-xsmall {{ $currency(product.price) }}

    template(v-else)
      .related-products-list
        .related-products-item(v-for="index in countForSkeletonLoader" :key="index")
          .related-products-item-inner.mb-150m.mt-50m
            .related-products-item-image
              .ssc-circle

            .related-products-item-desc.skeleton
              .ssc-line
              .ssc-line.mt-50m
              .xs-6.mt-50m
                .ssc-line
</template>

<script>
  import vueProductSvc from "@services/vueProductSvc";
  import SimpleCarouselSSR from '@components/SimpleCarouselSSR';
  import { getObjProperty } from '@utilities/mrVueUtils';
  import MrIcon from '@components/MrIcon';

  export default {
    name: 'RelatedProducts',

    components: {
      SimpleCarouselSSR,
      MrIcon
    },

    props: {
      variant: {
        type: String,
        default: 'inline',
        validator(value) {
          return ['inline', 'stacked', 'preview'].includes(value);
        }
      },

      title: {
        type: String,
        required: true,
      },

      products: {
        type: Array,
        required: true,
        default() {
          return [];
        }
      }
    },
    data() {
      return {
        productDetails: null,
        carouselOptions: {
          arrows: true,
          autoplay: false,
          infinite: false,
          slidesToShow: 3.5,
          dots: false,
          slidesToScroll: 1,
          accessibility: true,
          responsive: [
            {
              breakpoint: 560,
              settings: {
                arrows: true,
                dots: false,
                slidesToScroll: 1,
                slidesToShow: 1.5,
                centerMode: false,
                infinite: false,
                swipeToSlide: true,
                speed: 100
              }
            },
            {
              breakpoint: 720,
              settings: {
                arrows: true,
                dots: false,
                slidesToScroll: 1,
                slidesToShow: 2.25,
                swipeToSlide: true,
                speed: 100
              }
            },
            {
              breakpoint: 960,
              settings: {
                arrows: true,
                dots: false,
                infinite: false,
                swipeToSlide: true,
                speed: 100,
                slidesToShow: 2.5
              }
            },
            {
              breakpoint: 1256,
              settings: {
                arrows: true,
                dots: false,
                infinite: false,
                swipeToSlide: true,
                speed: 100,
                slidesToShow: 3.5
              }
            },
          ]
        },
      };
    },

    computed: {
      countForSkeletonLoader() {
        return this.variant === 'preview' ? 2 : 5;
      },

      firstTwoProducts() {
        if (!this.visibleProducts) {
          return [];
        }

        return this.visibleProducts.slice(0, 3);
      },

      visibleProducts() {
        return this.productDetails?.filter((product) => Boolean(product.visible)) || [];
      }
    },

    mounted() {
      vueProductSvc.getProductsById({ ids: this.products.map(({ id }) => id) }).then((productsRes) => {
        this.productDetails = productsRes.data;
      });
    },

    methods: {
      altText(product) {
        return `Madison Reed - ${product.name}`;
      },

      productUrl(urlKey) {
        return `/product/${urlKey}`;
      },

      imgSrc(product) {
        return getObjProperty(product, 'imagery.thumb.url');
      },

    }
  };
</script>

<style lang="stylus">
@import "~skeleton-screen-css/dist/index.min.css";
</style>

<style lang="stylus" scoped>
.related-products
  padding 1rem 0.5rem
  border 1px solid ui-color-5
  background-color #fff

  &.stacked
    padding 1rem 0
    border-left unset
    border-right unset
    border-top 1px solid ui-color-5
    border-bottom 1px solid ui-color-5

  &.preview
    padding 1rem 0
    border-left unset
    border-right unset
    border-bottom unset

  &.inline

    .related-products-title
      margin-left 1rem

  .related-products-title
    margin-bottom 1.5rem

  .related-products-list
    display flex
    flex-direction row
    flex-wrap wrap
    justify-content space-between

    &.stacked
      flex-direction column

      .related-products-item
        width 100%

    .simple-carousel
      height 100px

    .arrow-container
      display flex
      background white
      padding 0.8rem
      border-radius 2rem
      box-shadow 0 4px 4px rgba(0, 0, 0, 0.07)


    .related-products-item
      padding 0 0.6rem
      .related-products-item-inner
        display flex

      .related-products-item-image
        position relative
        img.prod-thumb
          width 4rem
          border-radius 8px

          &.preview
            width 2rem
            height 4rem
            object-fit cover

      .related-products-item-desc
        margin-left 0.8rem
        &.skeleton
          width 100px
        .related-products-item-name
          color cta-color-4
          margin-top 10px
          margin-bottom 5px
          text-decoration underline

        .related-products-item-price
          font-size 14px
          color #757575


</style>